import { assignInlineVars } from '@vanilla-extract/dynamic';
import clsx from 'clsx';
import React, {
  memo, ReactNode, useEffect, useMemo, useState,
} from 'react';

import { Icon } from '@ezugi/primitives';

import Elipse from './icons/elipse.svg';
import {
  container,
  containerShuffleBackground,
  timeLeftClassName,
  shuffleTime,
  progressCover,
  borderElipse,
  maskAndFill,
  fillDefault,
  fillLessThanHalf,
  fillLessThanQuarter,
  fillAnimationStyle,
  durationVar,
  PROGRESS,
  mask,
  secondsAnimation,
  startDegreesVar,
  containerBackgroundVar,
  childrenBackground,
  fillBackgroundVar,
  secondsAnimationVar,
} from './style.css';
import { getContainerBackground, getFillBackground, getSecondsAnimationDuration } from './style.util';

export type BettingTimerProps = Readonly<{
  initialTimeLeft: number;
  timeLeft: number;
  duration: number;
  isShuffleTime: boolean;
  className?: string;
  externalShowHideBehaviour?: boolean;
  children?: ReactNode;
}>;

/**
 * @component BettingTimer
 * @param {Object} props - Following props are accepted:
 * @param {number} props.initialTimeLeft - total time in ms (from GE)
 * @param {number} props.timeLeft - time left in ms (count down timer)
 * @param {number} props.duration - total time in ms
 * @param {boolean} props.isShuffleTime
 * @param {string} props.className
 * @param {boolean} props.externalShowHideBehaviour - used in Baccarat to keep component in DOM (opacity 0)
 */
function BettingTimer({
  timeLeft,
  duration,
  isShuffleTime,
  className,
  externalShowHideBehaviour,
  initialTimeLeft,
  children,
}: BettingTimerProps) {
  const timeLeftSeconds = timeLeft ? timeLeft / 1000 : 0;

  const progress = useMemo(() => {
    const progressValue = timeLeft ? duration / timeLeft : 0;

    if (progressValue >= 4) return PROGRESS.LESS_THAN_A_QUARTER;
    if (progressValue >= 2) return PROGRESS.LESS_THAN_HALF;

    return null;
  }, [ timeLeft, duration ]);

  const startDegrees = Math.round((initialTimeLeft / duration) * 180);

  // Flag needed to know if this was a shuffle round, even after shuffle time ends
  const [ isShuffleRound, setIsShuffleRound ] = useState(false);
  useEffect(() => {
    if (isShuffleTime) {
      setIsShuffleRound(true);
    }
    // Reset the flag at the beginning of a round
    if (timeLeft && timeLeft === duration) {
      setIsShuffleRound(false);
    }
  }, [ isShuffleTime, timeLeft, duration ]);

  if (!timeLeftSeconds && !isShuffleTime && !externalShowHideBehaviour) {
    return null;
  }

  const getFillStyle = () => {
    switch (progress) {
    case PROGRESS.LESS_THAN_HALF:
      return fillLessThanHalf;
    case PROGRESS.LESS_THAN_A_QUARTER:
      return fillLessThanQuarter;
    default:
      return fillDefault;
    }
  };

  return (
    <div
      data-e2e="betting-timer"
      data-testid="betting-timer"
      data-current-seconds={timeLeftSeconds}
      className={clsx(
        container,
        isShuffleRound && containerShuffleBackground,
        className
      )}
      style={{
        ...(externalShowHideBehaviour ? { opacity: 0 } : {}),
        ...assignInlineVars({
          [containerBackgroundVar]: getContainerBackground(isShuffleRound),
          [secondsAnimationVar]: getSecondsAnimationDuration(progress),
        }),
      }}
    >
      <Icon
        noDefaultStyling={true}
        size={48}
        className={borderElipse}
        svg={Elipse}
      />
      {children ? (
        <div className={childrenBackground}>
          {children}
        </div>
      ) : (
        <div
          data-testid="time-left"
          data-is-shuffle-time={!timeLeftSeconds && isShuffleRound}
          className={clsx(timeLeftClassName, {
            [shuffleTime]: !timeLeftSeconds && isShuffleRound,
          })}
        >
          <span className={clsx('seconds', {
            [secondsAnimation]: progress === PROGRESS.LESS_THAN_A_QUARTER,
          })}
          >
            {timeLeftSeconds}
          </span>
        </div>
      )}
      {!!timeLeft && !isShuffleRound && (
        <div
          data-testid="progress-cover"
          key={startDegrees}
          className={progressCover}
          style={assignInlineVars({
            [startDegreesVar]: `${startDegrees}deg`,
            [durationVar]: `${initialTimeLeft / 1000}s`,
            [fillBackgroundVar]: getFillBackground(progress),
          })}
        >
          <div className={clsx(mask, maskAndFill, fillAnimationStyle)}>
            <div
              className={clsx(
                maskAndFill,
                getFillStyle(),
                fillAnimationStyle
              )}
            />
          </div>
          <div className={clsx(mask, maskAndFill)}>
            <div
              className={clsx(
                maskAndFill,
                fillAnimationStyle,
                getFillStyle(),
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(BettingTimer);
